const STORAGE_KEY_PREFIX = "catalog_customer_order_draft_v1";

export function createDraftItemKey() {
  return `draft-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export function productToDraftItem(product, quantity) {
  const packingRate = product.packing_rate || 1;
  const qty = Math.max(packingRate, quantity);
  return {
    __key: createDraftItemKey(),
    product_id: product.id,
    supplier: product.supplier_name || "",
    supplier_id: product.supplier_id || "",
    brand: product.brand || "",
    article: product.articul || "",
    oem: product.oem || "",
    name: product.name || "",
    unit: product.unit || "",
    quantity: qty,
    price: product.client_price ?? 0,
    discount_percent: 0,
    comment: "",
    supplier_price: product.supplier_price ?? null,
  };
}

export function createEmptyCustomerOrderDraft() {
  return { items: [], client: null, discount_percent: 0 };
}

export function addProductToDraft(draft, product, quantity) {
  const existing = draft.items.find((item) => item.product_id === product.id);
  if (existing) {
    return {
      ...draft,
      items: draft.items.map((item) =>
        item.product_id === product.id
          ? { ...item, quantity: item.quantity + quantity }
          : item
      ),
    };
  }
  return {
    ...draft,
    items: [...draft.items, productToDraftItem(product, quantity)],
  };
}

export function updateDraftItem(draft, key, patch) {
  return {
    ...draft,
    items: draft.items.map((item) => (item.__key === key ? { ...item, ...patch } : item)),
  };
}

export function removeDraftItem(draft, key) {
  return {
    ...draft,
    items: draft.items.filter((item) => item.__key !== key),
  };
}

export function clearDraftItems(draft) {
  return { ...draft, items: [], discount_percent: 0 };
}

export function setDraftClient(draft, client) {
  return { ...draft, client: client || null };
}

export function setDraftOrderDiscount(draft, discountPercent) {
  return { ...draft, discount_percent: discountPercent };
}

export function sanitizeClientSnapshot(client) {
  if (!client || !client.id) return null;
  return {
    id: client.id,
    display_name: client.display_name || null,
    manual_name: client.manual_name || null,
    customer_name: client.customer_name || null,
    phone: client.phone || null,
    phone_normalized: client.phone_normalized || null,
    whatsapp_chat_id: client.whatsapp_chat_id || null,
    primary_vehicle: client.primary_vehicle || null,
    vehicles: client.vehicles || null,
    detected_car_model: client.detected_car_model || null,
  };
}

export function sanitizeDraftForStorage(draft) {
  const discountPercent = Number(draft.discount_percent);
  return {
    items: (draft.items || []).map((item) => ({
      __key: item.__key || createDraftItemKey(),
      product_id: item.product_id || "",
      supplier: item.supplier || "",
      supplier_id: item.supplier_id || "",
      brand: item.brand || "",
      article: item.article || "",
      oem: item.oem || "",
      name: item.name || "",
      unit: item.unit || "",
      quantity: Number(item.quantity) || 1,
      price: Number(item.price) || 0,
      discount_percent: Number(item.discount_percent) || 0,
      comment: String(item.comment || ""),
      supplier_price:
        item.supplier_price != null && item.supplier_price !== ""
          ? Number(item.supplier_price)
          : null,
    })),
    client: sanitizeClientSnapshot(draft.client),
    discount_percent:
      Number.isFinite(discountPercent) && discountPercent >= 0 && discountPercent <= 100
        ? discountPercent
        : 0,
  };
}

export function sanitizeDraftFromStorage(raw) {
  if (!raw || typeof raw !== "object") {
    return createEmptyCustomerOrderDraft();
  }
  const items = Array.isArray(raw.items)
    ? raw.items
        .filter((item) => item && (item.product_id || item.name))
        .map((item) => ({
          __key: item.__key || createDraftItemKey(),
          product_id: item.product_id || "",
          supplier: item.supplier || "",
          supplier_id: item.supplier_id || "",
          brand: item.brand || "",
          article: item.article || "",
          oem: item.oem || "",
          name: item.name || "",
          unit: item.unit || "",
          quantity: Number(item.quantity) > 0 ? Number(item.quantity) : 1,
          price: Number(item.price) >= 0 ? Number(item.price) : 0,
          discount_percent:
            Number(item.discount_percent) >= 0 && Number(item.discount_percent) <= 100
              ? Number(item.discount_percent)
              : 0,
          comment: String(item.comment || ""),
          supplier_price:
            item.supplier_price != null && item.supplier_price !== ""
              ? Number(item.supplier_price)
              : null,
        }))
    : [];
  const discountPercent = Number(raw.discount_percent);
  return {
    items,
    client: sanitizeClientSnapshot(raw.client),
    discount_percent:
      Number.isFinite(discountPercent) && discountPercent >= 0 && discountPercent <= 100
        ? discountPercent
        : 0,
  };
}

function storageKey(userId) {
  return `${STORAGE_KEY_PREFIX}:${userId}`;
}

export function loadDraftFromLocalStorage(userId) {
  if (!userId) return null;
  try {
    const raw = localStorage.getItem(storageKey(userId));
    if (!raw) return null;
    return sanitizeDraftFromStorage(JSON.parse(raw));
  } catch {
    return null;
  }
}

export function saveDraftToLocalStorage(userId, draft) {
  if (!userId) return;
  try {
    const sanitized = sanitizeDraftForStorage(draft);
    if (!sanitized.items.length && !sanitized.client) {
      localStorage.removeItem(storageKey(userId));
      return;
    }
    localStorage.setItem(storageKey(userId), JSON.stringify(sanitized));
  } catch {
    // ignore quota / private mode errors
  }
}

export function clearDraftLocalStorage(userId) {
  if (!userId) return;
  try {
    localStorage.removeItem(storageKey(userId));
  } catch {
    // ignore
  }
}

export function calculateDraftTotals(items, orderDiscountPercent = 0) {
  const lineTotals = items.reduce(
    (acc, item) => {
      const quantity = Number(item.quantity) || 0;
      const price = Number(item.price) || 0;
      const discountPercent = Number(item.discount_percent) || 0;
      const subtotal = price * quantity;
      const priceWithDiscount = price * (1 - discountPercent / 100);
      const total = priceWithDiscount * quantity;
      acc.subtotal += subtotal;
      acc.lineDiscount += subtotal - total;
      acc.itemsTotal += total;
      return acc;
    },
    { subtotal: 0, lineDiscount: 0, itemsTotal: 0 }
  );

  const orderDiscount =
    (lineTotals.itemsTotal * (Number(orderDiscountPercent) || 0)) / 100;
  const total = Math.max(0, lineTotals.itemsTotal - orderDiscount);

  return {
    subtotal: lineTotals.subtotal,
    lineDiscount: lineTotals.lineDiscount,
    orderDiscount,
    discount: lineTotals.lineDiscount + orderDiscount,
    total,
    itemsTotal: lineTotals.itemsTotal,
  };
}

export function calculateLineTotal(item) {
  const quantity = Number(item.quantity) || 0;
  const price = Number(item.price) || 0;
  const discountPercent = Number(item.discount_percent) || 0;
  const priceWithDiscount = price * (1 - discountPercent / 100);
  return priceWithDiscount * quantity;
}

export function draftItemsToApiPayload(items) {
  return items.map((item, index) => {
    const productName = (item.name || "").trim();
    return {
      product_id: item.product_id || null,
      product_code: item.oem || item.article || null,
      product_name: productName,
      quantity: Number(item.quantity),
      price: Number(item.price),
      discount_percent: Number(item.discount_percent) || 0,
      sort_order: index + 1,
      supplier_price_snapshot:
        item.supplier_price != null ? Number(item.supplier_price) : null,
      supplier_id_snapshot: item.supplier_id || null,
      comment: String(item.comment || "").trim() || null,
    };
  });
}

export function formatDraftMoney(value, currency = "KZT") {
  const n = Number(value);
  if (Number.isNaN(n)) return "—";
  return `${new Intl.NumberFormat("ru-RU", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(n)} ${currency === "KZT" ? "₸" : currency}`;
}
